import { Suspense, lazy, useLayoutEffect, useRef, type CSSProperties, type UIEvent } from 'react';
import type { OverlayScrollbars } from 'overlayscrollbars';
import type { OverlayScrollbarsComponentProps } from 'overlayscrollbars-react';

export type Axis = 'x' | 'y' | 'both';

export interface ScrollableProps extends Omit<OverlayScrollbarsComponentProps<'div'>, 'element'> {
  /** Scroll axis. Defaults to vertical. */
  axis?: Axis;
}

// OverlayScrollbars' custom scrollbars only earn their weight with a fine
// pointer (mouse / trackpad). Touch devices get the platform's native momentum
// scrolling instead and never download the OSB JS or CSS. `matchMedia` is
// absent in non-browser/test environments — treat that (and coarse pointers)
// as "use the native scroller".
function prefersNativeScroll(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(pointer: coarse)').matches;
}

const ScrollableOverlay = lazy(() => import('./ScrollableOverlay'));

const OVERFLOW_BY_AXIS: Record<Axis, CSSProperties> = {
  y: { overflowX: 'hidden', overflowY: 'auto' },
  x: { overflowX: 'auto', overflowY: 'hidden' },
  both: { overflow: 'auto' },
};

type OsbHandler = (instance: OverlayScrollbars) => void;

// OSB `events` handlers may be a single function or an array; the native
// fallback only needs to invoke them against a shim exposing the scroll host.
function callOsbEvent(handler: unknown, el: HTMLElement) {
  if (!handler) return;
  const shim = { elements: () => ({ viewport: el }) } as unknown as OverlayScrollbars;
  const handlers = Array.isArray(handler) ? handler : [handler];
  for (const fn of handlers) if (typeof fn === 'function') (fn as OsbHandler)(shim);
}

/**
 * Dependency-free native scroller used on touch devices. Reproduces enough of
 * the OSB lifecycle (`initialized`/`updated`/`scroll`) that consumers relying
 * on `events` — e.g. FilterChips edge fades — keep working without an instance.
 */
function NativeScrollable({ axis = 'y', events, className, style, children }: ScrollableProps) {
  // Only the props above are consumed on the native path; OSB-only props
  // (`options`/`defer`/`ref`) are intentionally not forwarded to the DOM node.
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !events) return;
    callOsbEvent(events.initialized, el);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => callOsbEvent(events.updated, el));
    ro.observe(el);
    return () => ro.disconnect();
  }, [events]);

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    if (events) callOsbEvent(events.scroll, e.currentTarget);
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...OVERFLOW_BY_AXIS[axis], ...style }}
      onScroll={events ? onScroll : undefined}
    >
      {children}
    </div>
  );
}

/**
 * Project scroll surface. On fine-pointer devices it renders the themed
 * OverlayScrollbars component (lazy-loaded); on touch it uses native overflow.
 * Call sites pass `className` for sizing and `children` for content.
 */
export function Scrollable(props: ScrollableProps) {
  if (prefersNativeScroll()) return <NativeScrollable {...props} />;
  return (
    <Suspense fallback={<NativeScrollable {...props} />}>
      <ScrollableOverlay {...props} />
    </Suspense>
  );
}
